/**
 * The packaged Android app, from the web app's point of view.
 *
 * The member SPA has NO `@capacitor/*` dependency and must not gain one: the
 * same bundle is served on the web, where those packages are dead weight and
 * their imports would fail. The Capacitor bridge injects `window.Capacitor` at
 * runtime instead, so everything here is a guarded probe of that global — which
 * is also why every function degrades to "not the native shell" rather than
 * throwing when it is absent.
 */

type CapacitorPlugins = {
  Filesystem?: {
    writeFile(o: {
      path: string;
      data: string;
      directory?: string;
      recursive?: boolean;
    }): Promise<{ uri: string }>;
  };
  Share?: {
    share(o: { title?: string; text?: string; url?: string; dialogTitle?: string }): Promise<void>;
  };
  Downloads?: {
    download(o: { url: string; filename?: string }): Promise<void>;
  };
  /** `@capacitor/local-notifications` (the coach app's shell). */
  LocalNotifications?: {
    checkPermissions(): Promise<{ display: string }>;
    requestPermissions(): Promise<{ display: string }>;
    schedule(o: {
      notifications: Array<{
        id: number;
        title: string;
        body: string;
        schedule?: { on?: { hour?: number; minute?: number }; allowWhileIdle?: boolean };
        extra?: Record<string, unknown>;
      }>;
    }): Promise<unknown>;
    cancel(o: { notifications: Array<{ id: number }> }): Promise<void>;
    addListener?(
      event: 'localNotificationActionPerformed',
      cb: (action: { notification?: { extra?: Record<string, unknown> } }) => void,
    ): Promise<ListenerHandle> | ListenerHandle;
  };
  /** `@capacitor/app`: lifecycle events. */
  App?: {
    addListener?(event: 'resume', cb: () => void): Promise<ListenerHandle> | ListenerHandle;
  };
};

type ListenerHandle = { remove(): Promise<void> | void };

type CapacitorWindow = Window & {
  Capacitor?: {
    isNativePlatform?: () => boolean;
    Plugins?: CapacitorPlugins;
  };
};

function cap() {
  return typeof window === 'undefined' ? undefined : (window as CapacitorWindow).Capacitor;
}

/** Running inside the packaged app rather than a browser tab. */
export function isNativeShell(): boolean {
  return cap()?.isNativePlatform?.() === true;
}

function plugins(): CapacitorPlugins | undefined {
  return cap()?.Plugins;
}

/** Is the plugin actually present? An APK built before it was added has none. */
export function canSaveFileNative(): boolean {
  const p = plugins();
  return isNativeShell() && Boolean(p?.Filesystem && p?.Share);
}

export function canDownloadUrlNative(): boolean {
  return isNativeShell() && Boolean(plugins()?.Downloads);
}

/**
 * Save bytes the page generated (a certificate PNG, a QR, a JSON export).
 *
 * Written to the app's CACHE directory and then handed to the system share
 * sheet, which is what lets the user put it wherever they want — Files, Drive,
 * a chat — without the app asking for a storage permission it does not need.
 * The cache directory is also self-cleaning, so a share the user cancels does
 * not leak a file forever.
 */
export async function saveFileNative(args: {
  filename: string;
  /** Base64 WITHOUT the `data:...;base64,` prefix. */
  base64: string;
  title?: string;
}): Promise<boolean> {
  const p = plugins();
  if (!p?.Filesystem || !p?.Share) return false;
  try {
    const { uri } = await p.Filesystem.writeFile({
      path: args.filename,
      data: args.base64,
      directory: 'CACHE',
      recursive: true,
    });
    await p.Share.share({ title: args.title ?? args.filename, url: uri });
    return true;
  } catch {
    // A cancelled share sheet rejects. Reporting that as a failure would show
    // an error to someone who simply changed their mind, so the caller only
    // learns that the native path was taken.
    return true;
  }
}

/**
 * Hand a URL to Android's DownloadManager.
 *
 * Used for presigned attachment links, which a WebView otherwise either ignores
 * or opens in the system browser — dumping the user out of the app to fetch a
 * file they asked for from inside it.
 */
export async function downloadUrlNative(args: {
  url: string;
  filename?: string;
}): Promise<boolean> {
  const p = plugins();
  if (!p?.Downloads) return false;
  try {
    await p.Downloads.download({ url: args.url, filename: args.filename });
    return true;
  } catch {
    return false;
  }
}

/** `data:image/png;base64,AAA…` → `AAA…`. Returns null for a non-data URL. */
export function base64FromDataUrl(dataUrl: string): string | null {
  const i = dataUrl.indexOf(',');
  return i > 0 && dataUrl.slice(0, i).includes('base64') ? dataUrl.slice(i + 1) : null;
}

/** Blob → bare base64, via FileReader (the only DOM API that does this). */
export function base64FromBlob(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(base64FromDataUrl(result) ?? '');
    };
    reader.readAsDataURL(blob);
  });
}

// ── Local notifications (the coach app's reminders) ───────────────────────────

/** Can this app schedule on-device reminders? Asks for the PLUGIN, like the file checks. */
export function canScheduleLocalNotifications(): boolean {
  return isNativeShell() && Boolean(plugins()?.LocalNotifications);
}

export type DailyReminder = {
  /** Stable per reminder: scheduling the same id again replaces it. */
  id: number;
  /** Device-local clock time it fires at, every day. */
  hour: number;
  minute: number;
  title: string;
  body: string;
  /** Carried to the tap handler (an in-app path to open). */
  extra?: Record<string, unknown>;
};

export type ReminderScheduleResult = 'scheduled' | 'denied' | 'unavailable';

/**
 * Schedule repeating daily reminders on the phone itself, no server involved.
 *
 * Permission is only ASKED FOR when `requestPermission` is true: the settings
 * screen asks when the member saves, but the quiet re-sync on every app resume
 * must never pop a permission dialog at someone who did not just ask for one.
 */
export async function scheduleDailyReminders(
  reminders: DailyReminder[],
  opts: { requestPermission?: boolean } = {},
): Promise<ReminderScheduleResult> {
  const p = plugins()?.LocalNotifications;
  if (!isNativeShell() || !p) return 'unavailable';
  if (reminders.length === 0) return 'scheduled';
  try {
    let { display } = await p.checkPermissions();
    if (display !== 'granted' && opts.requestPermission !== false) {
      ({ display } = await p.requestPermissions());
    }
    if (display !== 'granted') return 'denied';
    await p.schedule({
      notifications: reminders.map((r) => ({
        id: r.id,
        title: r.title,
        body: r.body,
        schedule: { on: { hour: r.hour, minute: r.minute }, allowWhileIdle: true },
        extra: r.extra,
      })),
    });
    return 'scheduled';
  } catch {
    return 'unavailable';
  }
}

/** Remove reminders by id. Unknown ids are fine; a missing plugin is a no-op. */
export async function cancelLocalNotifications(ids: number[]): Promise<void> {
  const p = plugins()?.LocalNotifications;
  if (!isNativeShell() || !p || ids.length === 0) return;
  try {
    await p.cancel({ notifications: ids.map((id) => ({ id })) });
  } catch {
    /* nothing scheduled under those ids */
  }
}

/** Subscribe to a plugin event whose `addListener` may return a handle or a promise of one. */
function listen(add: (() => Promise<ListenerHandle> | ListenerHandle) | undefined): () => void {
  if (!add) return () => undefined;
  let removed = false;
  let handle: ListenerHandle | null = null;
  try {
    const result = add();
    void Promise.resolve(result)
      .then((h) => {
        if (removed) void h.remove();
        else handle = h;
      })
      .catch(() => undefined);
  } catch {
    return () => undefined;
  }
  return () => {
    removed = true;
    if (handle) void handle.remove();
  };
}

/** Run `cb` whenever the app returns to the foreground. Returns an unsubscribe. */
export function onAppResume(cb: () => void): () => void {
  const app = isNativeShell() ? plugins()?.App : undefined;
  return listen(app?.addListener ? () => app.addListener!('resume', cb) : undefined);
}

/** Run `cb` with a reminder's `extra` when the member taps it. Returns an unsubscribe. */
export function onLocalNotificationTap(cb: (extra: Record<string, unknown>) => void): () => void {
  const p = isNativeShell() ? plugins()?.LocalNotifications : undefined;
  return listen(
    p?.addListener
      ? () =>
          p.addListener!('localNotificationActionPerformed', (action) =>
            cb(action.notification?.extra ?? {}),
          )
      : undefined,
  );
}
